import { usePageRuntime } from '@Pages/pageRuntime/pageRuntimeContextValue';
import type React from 'react';

type PermissionGateProps = {
  /** Content to render when the check passes. It does not mount when the check fails. */
  children?: React.ReactNode;

  /** Content to render when the check fails. Renders nothing when omitted. */
  fallback?: React.ReactNode;
};

type HasPermissionProps = PermissionGateProps & {
  /** Permission id or slug that the current identity must hold. */
  permission: string;
};

type HasAnyPermissionProps = PermissionGateProps & {
  /** Permission ids or slugs. The current identity must hold at least one. */
  permissions: readonly string[];
};

/**
 * Renders its children only when the current identity holds a permission, and the
 * `fallback` otherwise. The children do not mount when the check fails, so their hooks
 * and requests do not run. Adds no wrapper element.
 *
 * The check is the same literal membership test as `Sdk.permissions.has`: a system
 * administrator who does not hold the permission does not pass, and an anonymous viewer
 * never passes. This component only hides UI. Server-side route and policy enforcement
 * remains authoritative.
 *
 * @example
 * <Sdk.Components.HasPermission permission="write-off-invoices" fallback={<p>Ask a billing admin.</p>}>
 *   <WriteOffButton />
 * </Sdk.Components.HasPermission>
 */
export function HasPermission({ permission, children, fallback = null }: HasPermissionProps) {
  const permissions = usePageRuntime()?.Sdk.permissions;

  return <>{permissions?.has(permission) ? children : fallback}</>;
}

/**
 * Renders its children only when the current identity holds at least one of the
 * permissions, and the `fallback` otherwise. The children do not mount when the check
 * fails, so their hooks and requests do not run. Adds no wrapper element. To require
 * every permission, nest `HasPermission` components.
 *
 * The check is the same literal membership test as `Sdk.permissions.hasAny`: a system
 * administrator who holds none of the permissions does not pass, and an anonymous viewer
 * or an empty list never passes. This component only hides UI. Server-side route and
 * policy enforcement remains authoritative.
 *
 * @example
 * <Sdk.Components.HasAnyPermission permissions={['billing', 'admin']}>
 *   <WriteOffButton />
 * </Sdk.Components.HasAnyPermission>
 */
export function HasAnyPermission({
  permissions: requiredPermissions,
  children,
  fallback = null
}: HasAnyPermissionProps) {
  const permissions = usePageRuntime()?.Sdk.permissions;

  return <>{permissions?.hasAny(requiredPermissions) ? children : fallback}</>;
}
